import React, { useEffect, useRef, useState } from "react";
import "./LogoSlider.css";

export default function LogoSlider() {
  const canvasRef = useRef(null);
  const sliderContainerRef = useRef(null);
  const points = useRef([]);
  const maxDist = 200;
  const colour = "255,30,70";
  const mousePos = useRef(null);
  const [logos, setLogos] = useState([]);

  useEffect(() => {
    fetch("./assets/client-logos/list.json")
      .then((res) => res.json())
      .then((data) => setLogos(data));

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    function getMousePos(evt) {
      const rect = canvas.getBoundingClientRect();
      return {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top,
      };
    }

    canvas.addEventListener("mousemove", (evt) => {
      mousePos.current = getMousePos(evt);
    });

    function resizeCanvas() {
      canvas.width = sliderContainerRef.current?.offsetWidth;
      canvas.height = 100;
    }

    function point() {
      this.x = Math.random() * (canvas.width + maxDist) - maxDist / 2;
      this.y = Math.random() * (canvas.height + maxDist) - maxDist / 2;
      this.vx = Math.random() * 1 - 0.5;
      this.vy = Math.random() * 1 - 0.5;
      this.dia = Math.random() * 3 + 1;
      points.current.push(this);
    }

    function generatePoints(amount) {
      points.current = []; // Clear existing points
      for (let i = 0; i < amount; i++) {
        new point();
      }
    }

    function draw(obj) {
      ctx.beginPath();
      ctx.fillStyle = "rgb(" + colour + ")";
      ctx.arc(obj.x, obj.y, obj.dia || 2, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.fill();
    }

    function update(obj) {
      obj.x += obj.vx;
      obj.y += obj.vy;
      if (obj.x > canvas.width + maxDist / 2) obj.x = -(maxDist / 2);
      if (obj.x < -(maxDist / 2)) obj.x = canvas.width + maxDist / 2;
      if (obj.y > canvas.height + maxDist / 2) obj.y = -(maxDist / 2);
      if (obj.y < -(maxDist / 2)) obj.y = canvas.height + maxDist / 2;
    }

    function collision(obj, dist) {
      for (let i = 0; i < points.current.length; i++) {
        const p = points.current[i];
        if (obj !== p) {
          const temp = Math.hypot(obj.x - p.x, obj.y - p.y);
          if (temp < dist) {
            ctx.beginPath();
            ctx.strokeStyle =
              "rgba(" +
              colour +
              "," +
              0.8 * Math.pow((dist - temp) / dist, 5) +
              ")";
            ctx.moveTo(obj.x, obj.y);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
            ctx.closePath();
          }
        }
      }
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (mousePos.current) {
        collision(mousePos.current, maxDist * 2);
        draw(mousePos.current);
      }
      for (let i = 0; i < points.current.length; i++) {
        const p = points.current[i];
        collision(p, maxDist);
        draw(p);
        update(p);
      }
      requestAnimationFrame(animate);
    }

    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
      generatePoints(80);
    });

    resizeObserver.observe(sliderContainerRef.current);

    resizeCanvas();
    generatePoints(80);
    animate();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <div className="logo-slider-wrapper" ref={sliderContainerRef}>
      <canvas ref={canvasRef} className="canvas" />

      <div className="logo-slider-container">
        <div className="logo-track">
          {/* Double the logos for seamless infinite scrolling */}
          {[...logos, ...logos].map((logo, index) => (
            <img
              key={`${logo}-${index}`}
              src={`./assets/client-logos/${logo}.png`}
              alt={`Logo ${index + 1}`}
              loading="lazy"
            />
          ))}
        </div>
      </div>
    </div>
  );
}